// The GPU timer the client reports `gpuMs` from (R431, R505).
//
// The ring started inside `fast/frame.ts`. It moved here so the harness owns
// the measurement, because a second copy of a timer is a second set of
// figures to trust.
//
// `perf/` imports nothing from `fast/`, so the timer knows nothing of what a
// layer is. It takes a raw `WebGL2RenderingContext` and a predicate that
// answers whether the context is lost; the client wraps it as a
// `GpuResource`.
//
// The timer does not run unless the page asks for the harness.

/**
 * What `EXT_disjoint_timer_query_webgl2` gives. The DOM library has no type
 * for it, so the two constants the ring reads are named here.
 */
export interface TimerExt {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

/** How many queries are in flight before the ring stops starting new ones. */
const RING_QUERIES = 4;

/**
 * The WebGL2 GPU timer-query ring.
 *
 * `create` and `destroy` take the context, so a client that loses its
 * context can rebuild the ring in place. Everything else reads the context
 * the last `create` was given.
 */
export class GpuTimerRing {
  private gl: WebGL2RenderingContext | null = null;
  private ext: TimerExt | null = null;
  private free: WebGLQuery[] = [];
  private pending: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  /** The last completed measurement, in ms. */
  lastMs: number | null = null;

  /** `lost` answers whether the context is lost right now. */
  constructor(private readonly lost: () => boolean = () => false) {}

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    this.gl = gl;
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerExt | null;
    if (!this.ext) return;
    for (let i = 0; i < RING_QUERIES; i++) {
      const q = gl.createQuery();
      if (q) this.free.push(q);
    }
  }

  destroy(gl: WebGL2RenderingContext): void {
    for (const q of [...this.free, ...this.pending]) gl.deleteQuery(q);
    if (this.active) gl.deleteQuery(this.active);
    this.free = [];
    this.pending = [];
    this.active = null;
    this.ext = null;
    this.gl = null;
  }

  begin(): void {
    const gl = this.gl;
    if (!gl || !this.ext || this.active || this.free.length === 0 || this.lost()) return;
    const q = this.free.pop()!;
    this.active = q;
    gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
  }

  end(): void {
    const gl = this.gl;
    if (!gl || !this.ext || !this.active || this.lost()) return;
    gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  /** Reads whatever finished. A disjoint result is thrown away. */
  poll(): void {
    const gl = this.gl;
    if (!gl || !this.ext || this.lost()) return;
    const disjoint = gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const q = this.pending[i];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) continue;
      this.pending.splice(i, 1);
      if (!disjoint) {
        const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
        this.lastMs = ns / 1e6;
      }
      this.free.push(q);
    }
  }
}
